// quests.js FINAL optimized with cached questPlaces + fixed eatery-modal completion check

import { getFoodieProgress, toggleVisited, toggleMustTry, completeQuest, incompleteQuest } from './foodieProgress.js';
// import { checkAndAwardBadges } from './badges.js'; // DISABLED: Focusing on quests & must-try
import { launchConfetti } from './confetti.js';
import { openScannerModal, initScannerStyles } from './qr-scanner.js';

// existing visitedUpdated listener
document.addEventListener('visitedUpdated', async (e) => {
  console.log("visited Updated");
  const visited = e.detail.visited;

  // existing modal + quest progress updates...
  const checkboxes = document.querySelectorAll('#quest-restaurants-container input');
  const total = checkboxes.length;
  const done = [...checkboxes].filter(cb => cb.checked).length;
  const progressEl = document.getElementById('progress');
  if (progressEl) progressEl.textContent = `Visited ${done} of ${total}`;

  const questId = checkboxes[0]?.dataset.quest;
  const cardProgress = document.querySelector(`.quest-progress[data-id="${questId}"]`);
  if (cardProgress) cardProgress.textContent = `${done} of ${total}`;

  // Now also check for new badges
  // await checkAndAwardBadges(); // DISABLED: Focusing on quests & must-try

});

const DRUPAL_API_BASE = window.location.hostname.includes('localhost')
  ? 'http://localhost:8888'
  : 'https://ggcity.org';

let questMap = {};         // quest metadata (id -> name/image)
let questMetadata = [];    // raw quests array
let questPlacesMap = {};   // questId -> [placeIds]
let questDataPromise = null; // Shared promise to prevent duplicate fetches

// --- SINGLE OPTIMIZED FUNCTION: Load all quest data once ---
async function loadAllQuestData() {
  // Return existing promise if already loading
  if (questDataPromise) {
    console.log('⚡ Reusing existing quest data promise');
    return questDataPromise;
  }

  questDataPromise = (async () => {
    try {
      // Try cache first for instant load
      const cachedMetadata = localStorage.getItem('fogg_quest_metadata');
      const cachedPlaces = localStorage.getItem('fogg_quest_places');
      
      if (cachedMetadata && cachedPlaces) {
        const quests = JSON.parse(cachedMetadata);
        questPlacesMap = JSON.parse(cachedPlaces);
        
        quests.forEach(q => {
          questMap[q.id] = {
            name: q.name,
            machine_name: q.machine_name,
            image: `/images/quests/${q.machine_name}.png`
          };
        });
        questMetadata = quests;
        
        console.log('⚡ Loaded quest data from cache instantly');
        
        // Refresh in background (don't await)
        refreshQuestDataInBackground();
        
        return quests;
      }

      // No cache, fetch fresh data
      console.log('🔄 Fetching fresh quest data...');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const res = await fetch(`${DRUPAL_API_BASE}/api/fogg/foodie-quests`, {
        mode: 'cors',
        credentials: 'omit',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      questMetadata = await res.json();
      
      // Build questMap
      questMetadata.forEach(q => {
        questMap[q.id] = {
          name: q.name,
          machine_name: q.machine_name,
          image: `/images/quests/${q.machine_name}.png`
        };
      });
      
      // Cache metadata immediately
      try {
        localStorage.setItem('fogg_quest_metadata', JSON.stringify(questMetadata));
      } catch (e) {
        console.warn('Failed to cache quest metadata', e);
      }
      
      // Batch fetch places with delay to avoid rate limiting
      for (const quest of questMetadata) {
        try {
          const resPlaces = await fetch(`${DRUPAL_API_BASE}/api/fogg/foodie-quests/${quest.id}`, {
            mode: 'cors',
            credentials: 'omit'
          });
          
          if (resPlaces.ok) {
            const places = await resPlaces.json();
            questPlacesMap[quest.id] = places.map(p => p.id.toString());
          }
          
          // Small delay between requests to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 50));
          
        } catch (placeErr) {
          console.warn(`Failed to load places for quest ${quest.id}`, placeErr);
        }
      }
      
      console.log("✅ Loaded all quest data:", questMetadata.length, "quests");
      
      // Cache places
      try {
        localStorage.setItem('fogg_quest_places', JSON.stringify(questPlacesMap));
        localStorage.setItem('fogg_last_fetch', Date.now().toString());
      } catch (e) {
        console.warn('Failed to cache quest places', e);
      }
      
      return questMetadata;
      
    } catch (err) {
      console.error("Failed to load quest data", err);
      
      // Try to load from cache as fallback
      try {
        const cached = localStorage.getItem('fogg_quest_metadata');
        const cachedPlaces = localStorage.getItem('fogg_quest_places');
        
        if (cached) {
          const quests = JSON.parse(cached);
          if (cachedPlaces) {
            questPlacesMap = JSON.parse(cachedPlaces);
          }
          
          quests.forEach(q => {
            questMap[q.id] = {
              name: q.name,
              machine_name: q.machine_name,
              image: `/images/quests/${q.machine_name}.png`
            };
          });
          questMetadata = quests;
          
          console.log('✅ Loaded quest data from cache (after error)');
          return quests;
        }
      } catch (cacheErr) {
        console.error('Failed to load from cache', cacheErr);
      }
      
      throw err;
    }
  })();
  
  return questDataPromise;
}

// Background refresh (doesn't block UI)
async function refreshQuestDataInBackground() {
  try {
    const lastFetch = parseInt(localStorage.getItem('fogg_last_fetch') || '0');
    const fiveMinutes = 5 * 60 * 1000;
    
    // Only refresh if data is older than 5 minutes
    if (Date.now() - lastFetch < fiveMinutes) {
      console.log('⏭️ Skipping background refresh (data is fresh)');
      return;
    }
    
    console.log('🔄 Background refresh starting...');
    // DON'T call loadAllQuestData() again - that creates infinite loop!
    // Just fetch fresh data directly
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const res = await fetch(`${DRUPAL_API_BASE}/api/fogg/foodie-quests`, {
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    
    questMetadata = await res.json();
    
    // Update questMap
    questMetadata.forEach(q => {
      questMap[q.id] = {
        name: q.name,
        machine_name: q.machine_name,
        image: `/images/quests/${q.machine_name}.png`
      };
    });
    
    // Cache metadata
    localStorage.setItem('fogg_quest_metadata', JSON.stringify(questMetadata));
    localStorage.setItem('fogg_last_fetch', Date.now().toString());
    
    console.log('✅ Background refresh complete');
    
  } catch (err) {
    console.warn('Background refresh failed:', err);
  }
}

// --- Build quests slider ---
(async () => {
  const wrapper = document.getElementById('quests-wrapper');
  
  // Only run on quests page (check if wrapper exists)
  if (!wrapper) {
    console.log('⏭️ Skipping quest slider build (not on quests page)');
    return;
  }
  
  try {
    // Use single shared data load
    const quests = await loadAllQuestData();
    const wrapper = document.getElementById('quests-wrapper');
    quests.forEach(q => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.innerHTML = `
        <div class="quest-card" data-id="${q.id}">
          <div class="quest-inner">
            <div class="quest-image-container">
              <img src="/images/quests/${q.machine_name}.png" alt="${q.name}" class="quest-image" />
            </div>
            <h3 class="quest-title">${q.name}</h3>
            <div class="stats">
              <div class="stat-container">
                <div class="stat-area">
                  <div class="stat-data">
                    <div class="quest-progress" data-id="${q.id}">
                      <div class="progress-bar-container">
                        <div class="progress-bar-fill"></div>
                      </div>
                      <div class="progress-bar-text"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button class="play-quest-btn" data-id="${q.id}">
              View
            </button>
          </div>
        </div>
      `;
      wrapper.appendChild(slide);
    });

    // Check for ?quest= parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const targetQuestId = urlParams.get('quest');
    
    // Get last slide from localStorage or default to 0
    let initialSlideIndex = parseInt(localStorage.getItem('foodieQuestsLastSlide') || '0', 10);
    
    // If we have a target quest, find its index
    if (targetQuestId) {
      const targetIndex = quests.findIndex(q => q.id.toString() === targetQuestId);
      if (targetIndex !== -1) {
        initialSlideIndex = targetIndex;
      }
    }

    const swiper = new Swiper('.foodie-quests-swiper', {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 16,
      loop: true,
      grabCursor: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        dynamicBullets: true
      }, 
      initialSlide: initialSlideIndex
    });

    // Save new slide index whenever slide changes
    swiper.on('slideChange', () => {
      localStorage.setItem('foodieQuestsLastSlide', swiper.realIndex);
    });

    (async () => {
      // If we have a target quest, wait for questMap to load then open modal
      if (targetQuestId) {
        // Ensure questMap is ready
        while (Object.keys(questMap).length === 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        openQuestPopover(targetQuestId);
      }
      
      // Update progress in background
      await updateAllQuestProgress();
      await updateQuestCompletionUI();
    })();
  } catch (err) {
    console.error('Failed to load quests:', err);
    wrapper.innerHTML = '<p style="text-align:center; padding:20px;">Unable to load quests. Please check your connection.</p>';
    
    // Try to load from cache
    try {
      const cached = localStorage.getItem('fogg_quest_metadata');
      if (cached) {
        const quests = JSON.parse(cached);
        console.log('✅ Loaded quests from cache, rebuilding slider...');
        // Rebuild from cache would go here if needed
      }
    } catch (cacheErr) {
      console.error('Failed to load from cache', cacheErr);
    }
  }
})();

// --- UI helpers ---
function updateProgressDisplay(total, done) {
  const progressEl = document.getElementById('progress');
  if (!progressEl) return;

  const fill = progressEl.querySelector('.progress-bar-fill');
  const text = progressEl.querySelector('.progress-bar-text');

  const percentage = total > 0 ? (done / total) * 100 : 0;

  if (fill && text) {
    fill.style.width = percentage + '%';
    //text.textContent = `Visited ${done} of ${total}`;

    if (percentage === 100) {
      fill.classList.add('full');
    } else {
      fill.classList.remove('full');
    }
  } else {
    // fallback to text only
    progressEl.textContent = `Visited ${done} of ${total}`;
  }
}


// --- Open quest details modal ---
async function openQuestPopover(questId) {
  const modal = document.getElementById('quest-modal');
  modal.classList.remove('hidden');

  const container = document.getElementById('quest-restaurants-container');
  container.innerHTML = '';

  const image = document.getElementById('quest-modal-image');
  image.src = questMap[questId]?.image || '';
  image.alt = questMap[questId]?.name || '';
  image.className = 'quest-modal-image';

  let places = [];

  const res = await fetch(`${DRUPAL_API_BASE}/api/fogg/foodie-quests/${questId}`);
  places = await res.json();

  const { visited, mustTry } = await getFoodieProgress();

  places.forEach(place => {
    const placeId = place.id.toString();
    const isVisited = visited.includes(placeId);
    const isMustTry = mustTry.includes(placeId);
    const thumbnail = place.thumbnail || '';
    const div = document.createElement('div');
    div.className = isVisited ? 'place visited' : 'place';
    div.innerHTML = `
      <button
        class="place-title-btn"
        type="button"
        data-place-id="${placeId}"
        aria-label="View details for ${place.title}">
        ${thumbnail ? `<img src="${thumbnail}" alt="${place.title}" class="place-thumbnail" />` : ''}
        <span class="place-title-text">${place.title}</span>
      </button>
      <div class="place-status-badges">
        ${isVisited ? '<img src="/images/quests/scanned.png" alt="Visited" class="status-badge" />' : ''}
        ${isMustTry ? '<img src="/images/quests/try-on.png" alt="Must Try" class="status-badge" />' : ''}
      </div>
    `;
    container.appendChild(div);
  });
  
  const done = places.filter(p => visited.includes(p.id.toString())).length;
  updateProgressDisplay(places.length, done);
}

// --- Handle quest modal clicks ---
document.addEventListener('click', async (e) => {
  const card = e.target.closest('.quest-card');
  if (card) {
    const questId = card.getAttribute('data-id');
    openQuestPopover(questId);
  }

  const placeBtn = e.target.closest('.place-title-btn');
  if (placeBtn) {
    const placeId = placeBtn.dataset.placeId;
    openEateryModal(placeId);
  }
});


// Quest modal close with await
document.querySelector('.quest-modal-close').addEventListener('click', async () => {
  document.getElementById('quest-modal').classList.add('hidden');

  await updateAllQuestProgress();
});

// --- Update quest progress for all cards ---
async function updateAllQuestProgress() {
  const progressEls = document.querySelectorAll('.quest-progress');
  const { visited, mustTry, quests: completedQuests = [] } = await getFoodieProgress();
  const visitedSet = new Set(visited);
  const completedSet = new Set(completedQuests.map(q => q.toString()));

  for (const el of progressEls) {
    const questId = el.getAttribute('data-id');
    let total = 0;
    let done = 0;

    try {
      if (questPlacesMap[questId]) {
        const placeIds = questPlacesMap[questId];
        total = placeIds.length;
        done = placeIds.filter(id => visitedSet.has(id)).length;
      } else {
        const res = await fetch(`${DRUPAL_API_BASE}/api/fogg/foodie-quests/${questId}`);
        const places = await res.json();

        if (!Array.isArray(places)) {
          el.textContent = 'Error loading';
          continue;
        }

        total = places.length;
        done = places.filter(p => visitedSet.has(p.id.toString())).length;
      }

      // Update progress bar UI
      const fill = el.querySelector('.progress-bar-fill');
      const text = el.querySelector('.progress-bar-text');

      if (fill && text) {
        const percentage = total > 0 ? (done / total) * 100 : 0;
        fill.style.width = percentage + '%';
        text.textContent = `Visited ${done} of ${total}`;

        if (percentage === 100) {
          fill.classList.add('full');
        } else {
          fill.classList.remove('full');
        }
      } else {
        // fallback to text only
        el.textContent = `Visited ${done} of ${total}`;
      }

      // Auto-complete quests that are fully visited but not marked completed
      if (total > 0 && done === total && !completedSet.has(questId)) {
        console.log(`⚡ Auto-marking quest ${questId} as completed (visited all)`);
        await completeQuest(questId);
      }

    } catch (err) {
      console.error(`Error fetching progress for quest ${questId}:`, err);
      el.textContent = 'Error loading';
    }
  }

  // Finally, reapply UI styling for completed quests
  await updateQuestCompletionUI();
}


// --- Add 'completed' UI to completed quests ---
async function updateQuestCompletionUI() {
  const { quests: completedQuests = [] } = await getFoodieProgress();

  completedQuests.forEach(questId => {
    questId = questId.toString();

    const card = document.querySelector(`.quest-card[data-id="${questId}"]`);
    if (!card) return;

    const button = card.querySelector('.play-quest-btn');
    button.innerHTML = "Completed";

    if (!card.classList.contains('completed')) {
      card.classList.add('completed');

      // Add label if missing
      if (!card.querySelector('.quest-completed-label')) {
        const label = document.createElement('div');
        label.className = 'quest-completed-label';

        const i = document.createElement('i');
        i.className = "fa-regular fa-stars fa-2x";

        label.appendChild(i);
        card.appendChild(label);
      }
    }
  });
}

// --- Sync visited state ---
document.addEventListener('visitedUpdated', async (e) => {
  console.log('Visited updated event received, refreshing quest progress');
  // Refresh all quest progress displays
  await updateAllQuestProgress();
});

// --- Sync must try state ---
document.addEventListener('mustTryUpdated', (e) => {
  const list = e.detail.list;
  const buttons = document.querySelectorAll('.must-try-btn');
  buttons.forEach(btn => {
    const placeId = btn.dataset.placeId;
    btn.classList.toggle('active', list.includes(placeId));
  });
  
  // Update quest modal place-status-badges
  const questModal = document.getElementById('quest-modal');
  if (questModal && !questModal.classList.contains('hidden')) {
    const places = questModal.querySelectorAll('.place');
    places.forEach(place => {
      const placeBtn = place.querySelector('.place-title-btn');
      if (!placeBtn) return;
      
      const placeId = placeBtn.dataset.placeId;
      const statusBadges = place.querySelector('.place-status-badges');
      if (!statusBadges) return;
      
      const isMustTry = list.includes(placeId);
      
      // Remove existing must-try badge
      const existingMustTryBadge = statusBadges.querySelector('img[alt="Must Try"]');
      if (existingMustTryBadge) {
        existingMustTryBadge.remove();
      }
      
      // Add must-try badge if in list
      if (isMustTry) {
        const mustTryBadge = document.createElement('img');
        mustTryBadge.src = '/images/quests/try-on.png';
        mustTryBadge.alt = 'Must Try';
        mustTryBadge.className = 'status-badge';
        statusBadges.appendChild(mustTryBadge);
      }
    });
  }
});

// --- Eatery modal ---
export async function openEateryModal(placeId) {
  try {
    const loadingElem = document.getElementById('loading');
    loadingElem.style.display = 'block';
    loadingElem.style.visibility = 'visible';
    loadingElem.style.opacity = '1';

    const res = await fetch(`${DRUPAL_API_BASE}/api/fogg/restaurant/${placeId}`);
    const data = await res.json();
    
    // Cache restaurant data for later use (profile page, etc.)
    try {
      const cacheKey = `restaurant_${placeId}`;
      localStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Failed to cache restaurant data', e);
    }

    const modal = document.getElementById('eatery-modal');
    if (!modal) return;

    const heroImg = modal.querySelector('.eatery-hero-img');
    heroImg.src = data.hero_image || '';
    heroImg.alt = data.title || 'Eatery photo';

    modal.querySelector('.eatery-modal-title').textContent = data.title || 'Untitled';
    modal.querySelector('.eatery-meta').innerHTML = `
      <span class="eatery-type">${data.type || 'N/A'}</span><br>
      <span class="eatery-address">${data.address || ''}</span>
    `;

    const gallery = modal.querySelector('.eatery-gallery');
    gallery.innerHTML = '';
    if (Array.isArray(data.images)) {
      data.images.forEach(url => {
        const img = document.createElement('img');
        img.src = url;
        img.alt = `${data.title} photo`;
        img.loading = 'lazy';
        img.className = 'eatery-image';
        gallery.appendChild(img);
      });
    }

    const checkinBtn = modal.querySelector('.checkin-btn');
    const visitedBtn = modal.querySelector('.visited-btn');
    const mustTryBtn = modal.querySelector('.must-try-btn');
    
    checkinBtn.dataset.placeId = placeId;
    visitedBtn.dataset.placeId = placeId;
    mustTryBtn.dataset.placeId = placeId;

    const { visited, mustTry } = await getFoodieProgress();
    const isVisited = visited.includes(placeId.toString());
    
    // Show correct button based on visited status
    if (isVisited) {
      checkinBtn.style.display = 'none';
      visitedBtn.style.display = 'inline-flex';
    } else {
      checkinBtn.style.display = 'inline-flex';
      visitedBtn.style.display = 'none';
    }
    
    mustTryBtn.classList.toggle('active', mustTry.includes(placeId.toString()));

    loadingElem.style.display = 'none';

    modal.classList.remove('hidden');
    document.body.classList.add('modal-open');

  } catch (err) {
    console.error('Error opening eatery modal:', err);
  }
}

// --- Wire up modal toggle buttons ---
document.addEventListener('click', async (e) => {
  // Check In button - opens QR scanner
  const checkinBtn = e.target.closest('.checkin-btn');
  if (checkinBtn) {
    console.log('Opening QR scanner for check-in...');
    openScannerModal();
    return;
  }

  // Visited button - no action (just shows status)
  const visitedBtn = e.target.closest('.visited-btn');
  if (visitedBtn) {
    console.log('Already visited this eatery');
    return;
  }

  // Must-try button - toggles must-try status
  const mustTryBtn = e.target.closest('.must-try-btn');
  if (mustTryBtn) {
    const placeId = mustTryBtn.dataset.placeId;
    const updatedMustTry = await toggleMustTry(placeId);
    mustTryBtn.classList.toggle('active', updatedMustTry.includes(placeId));
    document.dispatchEvent(new CustomEvent('mustTryUpdated', { detail: { list: updatedMustTry } }));
    return;
  }
});

// Back button closes eatery modal
document.querySelector('.back-button').addEventListener('click', () => {
  document.getElementById('eatery-modal').classList.add('hidden');
  document.body.classList.remove('modal-open');
  updateAllQuestProgress();
    
});

// ✅ --- Optimized quest completion checker ---
async function checkQuestCompletionAfterVisited(placeId) {
  const { visited } = await getFoodieProgress(); // ✅ always fresh
  const placeStr = placeId.toString();

  // Find which quests contain this place
  const affectedQuests = Object.entries(questPlacesMap)
    .filter(([questId, placeIds]) => placeIds.includes(placeStr));

  if (!affectedQuests.length) {
    console.log("No quests affected by this place.");
    return;
  }

  for (const [questId, placeIds] of affectedQuests) {
    const total = placeIds.length;
    const done = placeIds.filter(id => visited.includes(id)).length;

    console.log(`Quest ${questId}: ${done}/${total}`);

    if (done === total && total > 0) {
      console.log(`🎉 Quest ${questId} fully completed from eatery modal!`);

      // Persist quest completion
      await completeQuest(questId);

      // Update quest card UI
      await updateQuestCompletionUI();

      // 🎊 Confetti feedback
      launchConfetti();
    }
    else {
      console.log(`Calling incomplete on Quest ${questId}`);
      await incompleteQuest(questId);
    }  
  }
}

// ✅ --- Fullscreen Lightbox ---
function setupLightbox() {
  const overlay = document.getElementById('lightbox-overlay');
  const overlayImg = document.getElementById('lightbox-img');
  const closeBtn = document.querySelector('.lightbox-close');

  document.body.addEventListener('click', (e) => {
    const img = e.target.closest('.eatery-image');
    if (!img) return;
    overlayImg.src = img.src;
    overlayImg.alt = img.alt || 'Full view';
    overlay.classList.remove('hidden');
  });

  closeBtn.addEventListener('click', () => {
    overlay.classList.add('hidden');
    overlayImg.src = '';
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.classList.add('hidden');
      overlayImg.src = '';
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.classList.contains('hidden')) {
      overlay.classList.add('hidden');
      overlayImg.src = '';
    }
  });
}

setupLightbox();

// Initialize QR scanner styles on page load
initScannerStyles();
